// Pure filter and sort pipeline for the milestone table.
//
// applyMilestoneFilters composes:
//   1. archived gate (drop archived unless includeArchived)
//   2. lifecycle-status filter (deriveMilestoneLifecycleStatus → membership)
//   3. awaitingAcceptance filter (deriveAwaitingAcceptance — broad: any
//      milestone delivered but not yet fully accepted, so the chip catches
//      both delivered-no-acceptance and partially-accepted rows)
//   4. SOW attachment filter
//   5. case-insensitive name search
//
// sortMilestones orders by derived status (upcoming < delivered <
// partially-accepted < accepted < paid) with plannedDate as secondary key.

import type { Milestone } from "../../../../types";
import {
  deriveMilestoneLifecycleStatus,
  deriveAwaitingAcceptance,
  type MilestoneDerivedStatus,
} from "../../../../utils/deriveMilestoneStatus";
import {
  UNATTACHED_TOKEN,
  type MilestoneFilterChipBarFilters,
} from "./milestoneUrlState";

const STATUS_ORDER: Record<MilestoneDerivedStatus, number> = {
  upcoming: 0,
  delivered: 1,
  "partially-accepted": 2,
  accepted: 3,
  paid: 4,
};

export function sortMilestones(milestones: readonly Milestone[]): Milestone[] {
  return [...milestones].sort((a, b) => {
    const rankA = STATUS_ORDER[deriveMilestoneLifecycleStatus(a)];
    const rankB = STATUS_ORDER[deriveMilestoneLifecycleStatus(b)];
    if (rankA !== rankB) return rankA - rankB;
    return a.plannedDate.localeCompare(b.plannedDate);
  });
}

export function applyMilestoneFilters(
  milestones: readonly Milestone[],
  filters: MilestoneFilterChipBarFilters,
  search: string,
): Milestone[] {
  const trimmedSearch = search.trim().toLowerCase();
  const statusFilter = filters.status;
  const wantsAwaitingAcceptance = filters.awaitingAcceptance;
  const sowFilter = filters.sow;
  const includeArchived = filters.includeArchived;

  return milestones.filter((m) => {
    if (!includeArchived && m.archivedAt) return false;
    if (statusFilter.length > 0) {
      const status = deriveMilestoneLifecycleStatus(m);
      if (!statusFilter.includes(status)) return false;
    }
    if (wantsAwaitingAcceptance && !deriveAwaitingAcceptance(m)) {
      return false;
    }
    if (sowFilter.length > 0) {
      const wantsUnattached = sowFilter.includes(UNATTACHED_TOKEN);
      const matchesUnattached = wantsUnattached && m.sowId == null;
      const matchesId = m.sowId != null && sowFilter.includes(m.sowId);
      if (!matchesUnattached && !matchesId) return false;
    }
    if (
      trimmedSearch.length > 0 &&
      !m.name.toLowerCase().includes(trimmedSearch)
    ) {
      return false;
    }
    return true;
  });
}
